import { clamp, isNil } from 'lodash';
import { Fragment, ReactNode, useLayoutEffect, useRef } from 'react';
import { View } from '@shared/components/View';
import { getColumnWidth } from '@shared/scenes/cards/tableView/utils/getColumnWidth';
import { API } from '@shared/types/api';
import styles from './SubtableColumnMeasure.module.css';

export const MIN_COLUMN_WIDTH = 80;

interface Props {
  columns: API.AnyComponent[];
  rows: API.WorkflowEntityFragment[];
  /** A column's header cell, as the table renders it, sized to its content. */
  renderHeader: (column: API.AnyComponent) => ReactNode;
  /** A row's cell in a column, as the table renders it, sized to its content. */
  renderCell: (row: API.WorkflowEntityFragment, column: API.AnyComponent) => ReactNode;
  /** Each column's width, in column order; called before the browser paints. */
  onMeasure: (widths: number[]) => void;
}

/**
 * A hidden layer that renders every column's header and loaded cells at their natural width,
 * reads each column's widest in a layout effect, and reports the widths, clamped between
 * MIN_COLUMN_WIDTH and the width the collection table gives that field type. Mounted only until
 * it has measured: SubtableTable renders it again when its rows or columns change.
 */
export function SubtableColumnMeasure({
  columns,
  rows,
  renderHeader,
  renderCell,
  onMeasure,
}: Props) {
  const layerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const columnElements = layerRef.current?.children;
    onMeasure(
      columns.map((column, index) => {
        const columnElement = columnElements?.[index];
        // The computed width is the layout width; getBoundingClientRect would include transforms,
        // like the expanded modal's scale while it animates open, and measure columns too narrow.
        const contentWidth = isNil(columnElement)
          ? 0
          : parseFloat(getComputedStyle(columnElement).width) || 0;
        return clamp(Math.ceil(contentWidth), MIN_COLUMN_WIDTH, getColumnWidth(column));
      }),
    );
  }, [columns, rows, onMeasure]);

  return (
    <View ref={layerRef} aria-hidden className={styles.subtableColumnMeasure}>
      {columns.map(column => (
        <View key={column.id} className={styles.column}>
          {renderHeader(column)}
          {rows.map(row => (
            <Fragment key={row.id}>{renderCell(row, column)}</Fragment>
          ))}
        </View>
      ))}
    </View>
  );
}
